import { useEffect, useRef, useState } from 'react';
import { SelectInput, required } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { apiFetch } from '../../api/client';

type Choice = { id: number; name: string };

async function loadChoices<T extends Choice>(path: string): Promise<T[]> {
  const res = await apiFetch<{ data: T[] }>(path);
  return res.data;
}

export function BusinessLocationInputs() {
  const { setValue } = useFormContext();
  const countryId = useWatch({ name: 'country_id' });
  const stateId = useWatch({ name: 'state_id' });
  const prevCountry = useRef<unknown>(countryId);
  const prevState = useRef<unknown>(stateId);

  const [categories, setCategories] = useState<Choice[]>([]);
  const [countries, setCountries] = useState<Choice[]>([]);
  const [states, setStates] = useState<Choice[]>([]);
  const [cities, setCities] = useState<Choice[]>([]);
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    void (async () => {
      const [cats, ctr] = await Promise.all([
        loadChoices('/admin/geo/categories'),
        loadChoices('/admin/geo/countries'),
      ]);
      setCategories(cats);
      setCountries(ctr);
      if (!countryId && ctr.length === 1) {
        setValue('country_id', ctr[0].id);
      }
    })();
  }, [countryId, setValue]);

  useEffect(() => {
    if (prevCountry.current !== undefined && prevCountry.current !== countryId) {
      setValue('state_id', undefined);
      setValue('city_id', undefined);
    }
    prevCountry.current = countryId;
    if (!countryId) {
      setStates([]);
      return;
    }
    void loadChoices(`/admin/geo/states?country_id=${countryId}`).then(setStates);
  }, [countryId, setValue]);

  useEffect(() => {
    if (prevState.current !== undefined && prevState.current !== stateId) {
      setValue('city_id', undefined);
    }
    prevState.current = stateId;
    if (!stateId) {
      setCities([]);
      return;
    }
    void loadChoices(`/admin/geo/cities?state_id=${stateId}`).then(setCities);
  }, [stateId, setValue]);

  useEffect(() => {
    void apiFetch<{ data: { id: string; name: string }[] }>('/admin/templates').then((res) =>
      setTemplates(res.data),
    );
  }, []);

  return (
    <>
      <SelectInput
        source="category_id"
        label="Category"
        choices={categories}
        optionText="name"
        optionValue="id"
        validate={[required()]}
        fullWidth
      />
      <SelectInput
        source="country_id"
        label="Country"
        choices={countries}
        optionText="name"
        optionValue="id"
        validate={[required()]}
      />
      <SelectInput
        source="state_id"
        label="State"
        choices={states}
        optionText="name"
        optionValue="id"
        validate={[required()]}
        disabled={!countryId}
      />
      <SelectInput
        source="city_id"
        label="City"
        choices={cities}
        optionText="name"
        optionValue="id"
        validate={[required()]}
        disabled={!stateId}
      />
      <SelectInput
        source="template_id"
        label="Template (optional)"
        choices={templates}
        optionText="name"
        optionValue="id"
        emptyText="— None —"
        fullWidth
      />
    </>
  );
}
